// The one way this client talks to parsina.bff.dentist.
//
// Mirrors the doctor client's src/lib/bffClient.js: attach the Keycloak access
// token, refresh once on a 401 and retry, and surface GraphQL errors WITH their
// `extensions` intact. There is no Apollo Client — a single fetch wrapper is the
// whole data layer, and both exported names are the same function.
//
// Why `extensions` must survive: this BFF's whole authorization story is carried
// in error codes (UNAUTHENTICATED, NOT_ONBOARDED, GATE_UNAVAILABLE,
// CLINIC_REQUIRED, INSUFFICIENT_CREDITS). Throwing on `res.status` alone would
// drop them, and the boot check would then read "your token expired" as "this
// dentist has not onboarded" — the exact regression the doctor client's comment
// records.

import { getToken, isTokenExpired, refreshAccessToken } from './auth.js';
import { emitSession, SessionEvent } from './session.js';
import { BFF_URL } from './runtimeEnv.js';

async function getValidToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    const ok = await refreshAccessToken();
    if (!ok) return null;
    token = getToken();
  }
  return token;
}

// The panel's language, so the BFF answers in it (its src/i18n/serverText.js):
// the services' errors and the labels they compose. `<html lang>` is set by the
// I18nProvider on every switch, so it is read per request.
function panelLanguage() {
  return (typeof document !== 'undefined' && document.documentElement.getAttribute('lang')) || 'fa';
}

async function executeRequest(token, query, variables) {
  return fetch(BFF_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-parsina-locale': panelLanguage(),
    },
    body: JSON.stringify({ query, variables }),
  });
}

/**
 * Build the Error a caller sees. Keeps `status`, `extensions` and the full
 * `graphQLErrors` list so a screen can branch on a code rather than on a string.
 */
function buildError(body, status) {
  const gqlError = body?.errors?.[0];
  const err = new Error(gqlError?.message || `BFF error: ${status}`);
  if (status) err.status = status;
  if (gqlError?.extensions) err.extensions = gqlError.extensions;
  if (body?.errors) err.graphQLErrors = body.errors;
  return err;
}

/**
 * The error a dead session throws.
 *
 * Carries the SAME shape a rejected request would (`status` + `extensions.code`)
 * so one branch handles both. App.jsx's boot runs before the shell — and so
 * before anything is listening for session events — and reads exactly these two
 * fields to decide "log in again" rather than "this dentist has not onboarded".
 */
function sessionEndedError(message) {
  const err = new Error(message);
  err.status = 401;
  err.extensions = { code: 'UNAUTHENTICATED' };
  return err;
}

async function bffRequest(query, variables = {}) {
  const token = await getValidToken();
  if (!token) {
    // Raise, do not navigate. `redirectToLogin()` here was a full-document
    // navigation, which unmounts React and destroys everything unsaved — a
    // طرح درمان mid-price, a tooth editor holding AI findings, a نسخه being
    // assembled — without warning and without returning the dentist to the
    // screen they were on. The shell listens for this and raises a dialog over
    // the still-intact screen instead (src/lib/session.js).
    emitSession(SessionEvent.EXPIRED);
    throw sessionEndedError('Not authenticated');
  }

  let res = await executeRequest(token, query, variables);

  // A 401 can also mean server-side expiry or clock skew, not just a stale
  // local token — refresh once and retry before giving up on the session.
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      emitSession(SessionEvent.EXPIRED);
      throw sessionEndedError('Session expired');
    }
    res = await executeRequest(getToken(), query, variables);
  }

  if (!res.ok) {
    // Read the body anyway: Apollo sends a perfectly normal GraphQL error
    // payload alongside a non-2xx status (401 UNAUTHENTICATED, 503
    // GATE_UNAVAILABLE), and the code is the only thing that tells them apart.
    const body = await res.json().catch(() => null);
    throw buildError(body, res.status);
  }

  const { data, errors } = await res.json();
  // A 200 with an `errors` entry is the field-level half of the specialty gate
  // (parsina.bff.dentist/src/graphql/guard.js) — deliberately not an HTTP error,
  // so a query that also asked for a permitted field still returns it.
  if (errors?.length) throw buildError({ errors });

  return data;
}

/** The GraphQL error code, when the failure carried one. */
export const errorCode = (err) => err?.extensions?.code || null;

export const bffQuery = bffRequest;
export const bffMutation = bffRequest;
